import { useCallback, useEffect, useState } from 'react';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button, Drawer, DrawerPosition, Heading } from '@savgroup-front-common/core';
import { toast } from '@savgroup-front-common/core/src/atoms/Toast';

const DRAWER_WIDTH_PERCENT = 0.33;
const DRAWER_WIDTH_MIN = 380;
const DRAWER_WIDTH_MAX = 720;

const getDrawerWidthPx = (): number => {
  if (typeof window === 'undefined') {
    return 480;
  }
  const w = Math.round(window.innerWidth * DRAWER_WIDTH_PERCENT);
  return Math.min(DRAWER_WIDTH_MAX, Math.max(DRAWER_WIDTH_MIN, w));
};

import { ClosedLock } from '@savgroup-front-common/core/src/protons/icons/Lock';
import { Accordion } from '@savgroup-front-common/core/src/molecules/Accordion';

import {
  $ActionOption,
  $ActionOptionLocked,
  $BackButton,
  $BackButtonWrap,
  $CloseButtonWrap,
  $ConfirmButtonWrap,
  $DrawerHeader,
  $LockIconWrap,
  $SelectedActionLabel,
  $SelectedActionStrip,
  $StepContent,
  $StepTitle,
  $UserSelect,
  $UserSelectLabel,
} from './DossierBulkActionDrawer.styles';

export const BULK_ACTION_TYPES = {
  CLOTURER: 'cloturer',
  AFFECTATION: 'affectation',
  RELANCE_ETIQUETTE: 'relance_etiquette',
  CHANGEMENT_STATUT: 'changement_statut',
  UPDATE_WORKFLOW_VERSION: 'update_workflow_version',
} as const;

export type BulkActionType = (typeof BULK_ACTION_TYPES)[keyof typeof BULK_ACTION_TYPES];

const ACTION_LABELS: Record<BulkActionType, string> = {
  [BULK_ACTION_TYPES.CLOTURER]: 'Clôturer des dossiers',
  [BULK_ACTION_TYPES.AFFECTATION]: 'Affectation en masse',
  [BULK_ACTION_TYPES.RELANCE_ETIQUETTE]: 'Relance d\'étiquette',
  [BULK_ACTION_TYPES.CHANGEMENT_STATUT]: 'Changement de statut',
  [BULK_ACTION_TYPES.UPDATE_WORKFLOW_VERSION]: 'Mettre à jour la version du workflow',
};

const LOCKED_ACTIONS: BulkActionType[] = [
  BULK_ACTION_TYPES.CHANGEMENT_STATUT,
];

export interface MockUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const MOCK_USERS: MockUser[] = [
  { id: '1', firstName: 'Marie', lastName: 'Martin', email: 'marie.martin@example.com' },
  { id: '2', firstName: 'Thomas', lastName: 'Bernard', email: 'thomas.bernard@example.com' },
  { id: '3', firstName: 'Sophie', lastName: 'Dubois', email: 'sophie.dubois@example.com' },
  { id: '4', firstName: 'Lucas', lastName: 'Petit', email: 'lucas.petit@example.com' },
  { id: '5', firstName: 'Emma', lastName: 'Leroy', email: 'emma.leroy@example.com' },
  { id: '6', firstName: 'Hugo', lastName: 'Moreau', email: 'hugo.moreau@example.com' },
];

export interface DossierBulkActionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCount: number;
}

interface WorkflowVersion {
  id: string;
  label: string;
}

interface WorkflowSetDefinition {
  id: string;
  workflowName: string;
  versions: WorkflowVersion[];
}

interface SellerWorkflowGroup {
  id: string;
  sellerName: string;
  workflowSets: WorkflowSetDefinition[];
}

const MOCK_SELLER_WORKFLOWS: SellerWorkflowGroup[] = [
  {
    id: 'seller-sav',
    sellerName: 'SAV Group',
    workflowSets: [
      {
        id: 'set-reparation',
        workflowName: 'Workflow Réparation',
        versions: [
          { id: 'sav-rep-v1', label: "Version 1, date d'import : 15/09/2025" },
          { id: 'sav-rep-v2', label: "Version 2, date d'import : 28/10/2025" },
          { id: 'sav-rep-v3', label: "Version 3, date d'import : 05/12/2025" },
        ],
      },
      {
        id: 'set-installation',
        workflowName: 'Workflow Installation',
        versions: [
          { id: 'sav-inst-v1', label: "Version 1, date d'import : 02/08/2025" },
          { id: 'sav-inst-v2', label: "Version 2, date d'import : 10/11/2025" },
        ],
      },
    ],
  },
  {
    id: 'seller-acme',
    sellerName: 'ACME Retail',
    workflowSets: [
      {
        id: 'set-echange',
        workflowName: 'Workflow Échange Standard',
        versions: [
          { id: 'acme-ex-v1', label: "Version 1, date d'import : 21/07/2025" },
          { id: 'acme-ex-v2', label: "Version 2, date d'import : 01/10/2025" },
        ],
      },
      {
        id: 'set-relance',
        workflowName: 'Workflow Relance',
        versions: [
          { id: 'acme-rel-v1', label: "Version 1, date d'import : 12/09/2025" },
        ],
      },
    ],
  },
];

const DossierBulkActionDrawer = ({
  isOpen,
  onClose,
  selectedCount,
}: DossierBulkActionDrawerProps) => {
  const [drawerWidth, setDrawerWidth] = useState(getDrawerWidthPx);
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedAction, setSelectedAction] = useState<BulkActionType | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedVersionsBySetId, setSelectedVersionsBySetId] = useState<Record<string, string>>(
    {},
  );
  const [openSellerIds, setOpenSellerIds] = useState<Set<string>>(
    () => new Set(MOCK_SELLER_WORKFLOWS.map((s) => s.id)),
  );

  useEffect(() => {
    const updateWidth = () => setDrawerWidth(getDrawerWidthPx());
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const handleClose = useCallback(() => {
    setStep(1);
    setSelectedAction(null);
    setSelectedUserId('');
    setSelectedVersionsBySetId({});
    setOpenSellerIds(new Set(MOCK_SELLER_WORKFLOWS.map((s) => s.id)));
    onClose();
  }, [onClose]);

  const handleSelectAction = useCallback((action: BulkActionType) => {
    if (action === BULK_ACTION_TYPES.RELANCE_ETIQUETTE) {
      handleClose();
      toast.success(
        <span>
          Action enregistrée, pour plus de détails{' '}
          <button
            type="button"
            onClick={() => alert('Détails de la relance d’étiquette (exemple).')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
              font: 'inherit',
            }}
          >
            cliquer ici
          </button>
        </span>,
        { autoClose: 6000, showIcon: true },
      );
      return;
    }

    setSelectedAction(action);
    setStep(2);
  }, [handleClose]);

  const handleBack = useCallback(() => {
    setStep(1);
    setSelectedAction(null);
    setSelectedUserId('');
    setSelectedVersionsBySetId({});
    setOpenSellerIds(new Set(MOCK_SELLER_WORKFLOWS.map((s) => s.id)));
  }, []);

  const allWorkflowSets = MOCK_SELLER_WORKFLOWS.flatMap((s) => s.workflowSets);
  const hasAllRequiredVersionsSelected =
    allWorkflowSets.length > 0 &&
    allWorkflowSets.every((set) => Boolean(selectedVersionsBySetId[set.id]));

  const handleConfirm = useCallback(() => {
    if (selectedAction === BULK_ACTION_TYPES.AFFECTATION && selectedUserId) {
      const user = MOCK_USERS.find((u) => u.id === selectedUserId);
      alert(`Affectation en masse : ${selectedCount} dossier(s) → ${user?.firstName} ${user?.lastName} (${user?.email})`);
    } else if (
      selectedAction === BULK_ACTION_TYPES.UPDATE_WORKFLOW_VERSION &&
      hasAllRequiredVersionsSelected
    ) {
      const selections = MOCK_SELLER_WORKFLOWS.map((seller) => {
        const sets = seller.workflowSets
          .map((set) => {
            const versionId = selectedVersionsBySetId[set.id];
            const versionLabel =
              set.versions.find((v) => v.id === versionId)?.label ?? versionId;

            return `${set.workflowName} / ${versionLabel}`;
          })
          .join(' | ');

        return `${seller.sellerName} → ${sets}`;
      }).join('\n');

      alert(
        `Mise à jour de la version du workflow : ${selectedCount} dossier(s)\n${selections}`,
      );
    } else {
      alert(`Action "${selectedAction}" sur ${selectedCount} dossier(s) (exemple non implémenté).`);
    }
    handleClose();
  }, [
    selectedAction,
    selectedUserId,
    hasAllRequiredVersionsSelected,
    selectedVersionsBySetId,
    selectedCount,
    handleClose,
  ]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      position={DrawerPosition.Right}
      width={drawerWidth}
      dataTestId="dossier-bulk-action-drawer"
    >
      <$DrawerHeader>
        <Heading level={2}>Action en masse</Heading>
        <$CloseButtonWrap>
          <Button
            type={BUTTON_TYPES.BUTTON}
            tertiary
            onClick={handleClose}
            dataTestId="drawer-close"
            ariaLabel={{ id: 'drawer.close', defaultMessage: 'Fermer' }}
          >
            ✕
          </Button>
        </$CloseButtonWrap>
      </$DrawerHeader>

      <$StepContent>
        {step === 1 && (
          <>
            <$StepTitle>Choisir le type d&apos;action</$StepTitle>
            {(Object.keys(ACTION_LABELS) as BulkActionType[]).map((action) =>
              LOCKED_ACTIONS.includes(action) ? (
                <$ActionOptionLocked
                  key={action}
                  data-testid={`action-${action}`}
                  title="Action non disponible (permission requise)"
                >
                  <$LockIconWrap aria-hidden>
                    <ClosedLock size="18px" color="#888" />
                  </$LockIconWrap>
                  {ACTION_LABELS[action]}
                </$ActionOptionLocked>
              ) : (
                <$ActionOption
                  key={action}
                  type="button"
                  onClick={() => handleSelectAction(action)}
                  data-testid={`action-${action}`}
                >
                  {ACTION_LABELS[action]}
                </$ActionOption>
              ),
            )}
          </>
        )}

        {step === 2 && selectedAction && (
          <>
            <$SelectedActionStrip>
              <$SelectedActionLabel>Action sélectionnée :</$SelectedActionLabel>
              {ACTION_LABELS[selectedAction]}
            </$SelectedActionStrip>
            <$BackButtonWrap>
              <$BackButton type="button" onClick={handleBack} data-testid="drawer-back">
                ← Retour
              </$BackButton>
            </$BackButtonWrap>
            <$StepTitle>Paramètres</$StepTitle>

            {selectedAction === BULK_ACTION_TYPES.AFFECTATION && (
              <div style={{ marginTop: '1rem' }}>
                <$UserSelectLabel htmlFor="bulk-user-select">Utilisateur</$UserSelectLabel>
                <$UserSelect
                  id="bulk-user-select"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  data-testid="bulk-user-select"
                >
                  <option value="">Sélectionner un utilisateur</option>
                  {MOCK_USERS.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.firstName} {u.lastName} ({u.email})
                    </option>
                  ))}
                </$UserSelect>
              </div>
            )}

            {selectedAction === BULK_ACTION_TYPES.UPDATE_WORKFLOW_VERSION && (
              <div style={{ marginTop: '1rem' }}>
                {MOCK_SELLER_WORKFLOWS.map((seller) => (
                  <Accordion
                    key={seller.id}
                    title={seller.sellerName}
                    isOpen={openSellerIds.has(seller.id)}
                    onClick={() =>
                      setOpenSellerIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(seller.id)) {
                          next.delete(seller.id);
                        } else {
                          next.add(seller.id);
                        }
                        return next;
                      })
                    }
                    dataTestId={`workflow-seller-${seller.id}`}
                  >
                    <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {seller.workflowSets.map((set) => (
                        <div key={set.id}>
                          <$UserSelectLabel htmlFor={`workflow-set-${set.id}`}>
                            {set.workflowName}
                            <span style={{ color: '#d32f2f' }}>&nbsp;*</span>
                          </$UserSelectLabel>
                          <$UserSelect
                            id={`workflow-set-${set.id}`}
                            value={selectedVersionsBySetId[set.id] ?? ''}
                            onChange={(e) => {
                              const nextVersionId = e.target.value;
                              setSelectedVersionsBySetId((prev) => ({
                                ...prev,
                                [set.id]: nextVersionId,
                              }));
                            }}
                            data-testid={`workflow-set-${set.id}`}
                          >
                            <option value="">Sélectionner une version</option>
                            {set.versions.map((v) => (
                              <option key={v.id} value={v.id}>
                                {v.label}
                              </option>
                            ))}
                          </$UserSelect>
                        </div>
                      ))}
                    </div>
                  </Accordion>
                ))}
              </div>
            )}

            {selectedAction === BULK_ACTION_TYPES.CLOTURER && (
              <p style={{ marginTop: '1rem', color: '#666' }}>
                Options de clôture à venir (exemple).
              </p>
            )}

            {selectedAction === BULK_ACTION_TYPES.RELANCE_ETIQUETTE && (
              <p style={{ marginTop: '1rem', color: '#666' }}>
                Options de relance d&apos;étiquette à venir (exemple).
              </p>
            )}

            {selectedAction === BULK_ACTION_TYPES.CHANGEMENT_STATUT && (
              <p style={{ marginTop: '1rem', color: '#666' }}>
                Choix du nouveau statut à venir (exemple).
              </p>
            )}

            <$ConfirmButtonWrap>
              <Button
                type={BUTTON_TYPES.BUTTON}
                primary
                onClick={handleConfirm}
                dataTestId="drawer-confirm"
                disabled={
                  (selectedAction === BULK_ACTION_TYPES.AFFECTATION && !selectedUserId) ||
                  (selectedAction === BULK_ACTION_TYPES.UPDATE_WORKFLOW_VERSION &&
                    !hasAllRequiredVersionsSelected)
                }
              >
                Confirmer
              </Button>
            </$ConfirmButtonWrap>
          </>
        )}
      </$StepContent>
    </Drawer>
  );
};

export default DossierBulkActionDrawer;
